import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import blogs from '../../data/blogs';
import './Blog.css';

const CLUSTER_COLORS = ['c0', 'c1', 'c2', 'c3', 'c4'];

const clusterIndex = (() => {
  const map = {};
  let i = 0;
  blogs.forEach((b) => {
    if (!(b.cluster in map)) map[b.cluster] = i++ % CLUSTER_COLORS.length;
  });
  return map;
})();

const Blog = () => {
  const navigate = useNavigate();
  const [activeCluster, setActiveCluster] = useState('All');

  const sorted = [...blogs].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
  const pinned = sorted.find((p) => p.pinned);
  const rest = sorted.filter((p) => !p.pinned);

  const clusters = ['All', ...Array.from(new Set(blogs.map((b) => b.cluster)))];
  const filtered = activeCluster === 'All' ? rest : rest.filter((p) => p.cluster === activeCluster);

  return (
    <div className="bl-page">
      <Helmet>
        <title>dmyor Blog — On Intentions, Long-Term Thinking, and Making Things Real</title>
        <link rel="canonical" href="https://www.dmyor.com/blog" />
        <meta name="description" content="Guides on how to use dmyor, write strong intentions, plan long-term, and document your progress the right way." />
        <meta property="og:title" content="dmyor Blog — On Intentions, Long-Term Thinking, and Making Things Real" />
        <meta property="og:description" content="Guides on how to use dmyor, write strong intentions, plan long-term, and document your progress the right way." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dmyor.com/blog" />
        <meta property="og:image" content="https://dmyor.com/images/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="dmyor Blog — On Intentions, Long-Term Thinking, and Making Things Real" />
        <meta name="twitter:description" content="Guides on how to use dmyor, write strong intentions, plan long-term, and document your progress the right way." />
        <meta name="twitter:image" content="https://dmyor.com/images/og-image.png" />
      </Helmet>

      {/* ── HEADER ── */}
      <header className="bl-header">
        <div className="bl-header-blob-a" aria-hidden="true" />
        <div className="bl-header-blob-b" aria-hidden="true" />
        <div className="bl-header-blob-c" aria-hidden="true" />
        <div className="bl-header-inner">
          <span className="bl-eyebrow">⌁ from the build</span>
          <h1 className="bl-title">
            Writings on <span className="bl-title-accent">making things real.</span>
          </h1>
          <p className="bl-subtitle">
            Every post here is about one thing: turning what's in your head into something that actually exists.
          </p>
        </div>
      </header>

      <div className="bl-body">

        {/* ── FEATURED (pinned) ── */}
        {pinned && (
          <div className="bl-featured-wrap">
            <div className="bl-featured" onClick={() => navigate(`/blog/${pinned.slug}`)}>
              <div className="bl-featured-meta">
                <span className="bl-tag bl-tag--featured">Featured</span>
                <span className={`bl-tag bl-tag--c${clusterIndex[pinned.cluster] ?? 0}`}>{pinned.cluster}</span>
              </div>
              <h2 className="bl-featured-title">{pinned.title}</h2>
              <p className="bl-featured-excerpt">{pinned.excerpt}</p>
              <div className="bl-featured-footer">
                <span className="bl-date">{pinned.date}</span>
                <span className="bl-dot">·</span>
                <span className="bl-read">{pinned.readTime}</span>
                <span className="bl-read-arrow">Read →</span>
              </div>
            </div>
          </div>
        )}

        {/* ── FILTERS ── */}
        {clusters.length > 2 && (
          <div className="bl-filters">
            {clusters.map((c) => (
              <button
                key={c}
                className={`bl-filter${activeCluster === c ? ' bl-filter--active' : ''}`}
                onClick={() => setActiveCluster(c)}
                type="button"
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {/* ── GRID ── */}
        <div className="bl-grid">
          {filtered.length === 0 ? (
            <p className="bl-empty">No posts in this category yet.</p>
          ) : (
            filtered.map((post) => {
              const ci = clusterIndex[post.cluster] ?? 0;
              return (
                <article
                  key={post.slug}
                  className={`bl-card bl-card--c${ci}`}
                  onClick={() => navigate(`/blog/${post.slug}`)}
                >
                  <div className="bl-card-top">
                    <div className="bl-card-meta">
                      <span className={`bl-tag bl-tag--c${ci}`}>{post.cluster}</span>
                      <span className="bl-date">{post.date}</span>
                    </div>
                    <h2 className="bl-card-title">{post.title}</h2>
                    <p className="bl-card-excerpt">{post.excerpt}</p>
                  </div>
                  <div className="bl-card-footer">
                    <span className="bl-read">{post.readTime}</span>
                    <span className="bl-card-arrow">→</span>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Blog;
